import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Loader2, Plus } from "lucide-react";
import { api, formatApiError } from "@/api/client";

/**
 * Admin Studio — Assessment list (Phase 1d).
 * Route: /admin/academy/assessments
 * RBAC: view_academy + academy_admin_studio_enabled.
 */
export default function AdminAssessmentsList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await api.get(`/admin/academy/assessments`);
      setItems(res.data.items || res.data || []);
      setStatus("ready");
    } catch (err) {
      setError(formatApiError(err));
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-academy-assessments-list" className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <header className="mb-8 flex items-start justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">Academy Studio</p>
          <h1
            className="mt-1 text-[32px] font-semibold text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
          >
            Assessments
          </h1>
          <p className="mt-2 text-sm text-v2-text-muted">
            Author quizzes and final exams. New assessments are always draft
            until you explicitly publish.
          </p>
        </div>
      </header>

      {status === "loading" ? (
        <div className="flex items-center gap-2 text-v2-text-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : status === "error" ? (
        <div className="rounded-xl border border-[var(--v2-danger)] p-6 text-v2-text">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="admin-academy-assessments-empty"
          className="rounded-xl border border-v2-border bg-v2-surface p-10 text-center"
        >
          <ClipboardList className="w-8 h-8 mx-auto text-v2-text-muted" />
          <p className="mt-3 text-v2-text font-semibold">No assessments yet</p>
          <p className="mt-1 text-sm text-v2-text-muted">
            Create one from a program or module page.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.id}>
              <Link
                to={`/admin/academy/assessments/${a.id}`}
                data-testid={`admin-academy-assessment-${a.id}`}
                className="rounded-xl border border-v2-border bg-v2-surface p-4 flex items-center justify-between hover:border-v2-accent"
              >
                <div className="min-w-0">
                  <p className="text-v2-text font-medium truncate">{a.title}</p>
                  <p className="text-xs text-v2-text-muted mt-1">
                    {a.kind} · {a.scope} · {a.status}
                    {a.pass_pct != null ? ` · pass ${a.pass_pct}%` : ""}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-v2-text-muted" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
